import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Divider, Spin, message } from 'antd';
import { HeartFilled, MedicineBoxFilled, TeamOutlined, RightOutlined, CalendarOutlined, EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { ROUTES } from '../../constants/routes';
import EventDetailsModal from '../../components/EventDetailsModal';
import EventService from '../../services/EventService';
import BookingService from '../../services/BookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import {
  HomeContainer,
  LogoContainer,
  StyledTitle,
  IntroText,
  LoadingContainer,
  LoadingText,
  EventsRow,
  EventCard,
  EventContainer,
  EventTitle,
  EventDescription,
  EventMeta,
  MetaItem,
  EmptyEventsContainer,
  EmptyEventsIcon,
  EmptyEventsTitle,
  EmptyEventsText,
  FormsRow,
  StyledCard,
  CardIcon,
  CardDescription,
  CardDetails,
  CardDetailText,
  CardButtonContainer,
  ActionButton,
  SectionTitle,
  FooterText
} from './styled';

const HomeScreen = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventModalVisible, setEventModalVisible] = useState(false);

  useEffect(() => {
    loadUpcomingEvents();
  }, []);  const loadUpcomingEvents = async () => {
    try {
      setLoading(true);
      
      // Fetch public booking events (bookings are the events)
      const publicEventsResponse = await BookingService.getUpcomingPublicEvents();
      
      console.log('Public events response:', publicEventsResponse);
      
      // Get public booking events
      const events = publicEventsResponse.success ? (publicEventsResponse.data || []) : [];
      
      // Sort by date and limit to 6 events
      const sortedEvents = events
        .sort((a, b) => new Date(a.startDate || a.date) - new Date(b.startDate || b.date))
        .slice(0, 6);
      
      console.log('Upcoming booking events:', sortedEvents);
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      message.error('Failed to load upcoming events');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventModalVisible(true);
  };

  const handleEventModalClose = () => {
    setEventModalVisible(false);
    setSelectedEvent(null);
  };

  const handleEventRegistration = async (registrationData) => {
    try {
      await EventService.registerForEvent(selectedEvent._id, registrationData);
      message.success('Successfully registered for the event!');
      handleEventModalClose();
      loadUpcomingEvents(); // Refresh events to update registration count
    } catch (error) {
      console.error('Error registering for event:', error);
      message.error('Failed to register for event. Please try again.');
    }
  };
  
  const goToLPATest = () => {
    navigate(ROUTES.LPA_TEST);
  };
  
  const goToBusDataForm = () => {
    navigate(ROUTES.BUS_SCREENING);
  };
  
  const goToVolunteerForm = () => {
    navigate(ROUTES.VOLUNTEER);
  };

  return (
    <HomeContainer>      
      <LogoContainer>
        <img src="/bha-original-logo.png" alt="Black Heart Association Logo" />
      </LogoContainer>
        <StyledTitle level={2}>Guard Your Heart Screening Program</StyledTitle>      <IntroText>
        Select the appropriate form for your heart health screening needs
      </IntroText>
      
        <FormsRow gutter={[32, 32]}>
        <Col xs={24} md={8}>
          <StyledCard 
            headcolor="#000000" 
            bordercolor="#000000"
            title="Lipoprotein(a) Test Request"
          >
            <CardIcon color="#000000">
              <HeartFilled />
            </CardIcon>
            
            <CardDescription>
              Request a laboratory blood test for Lipoprotein(a), an independent risk factor for 
              cardiovascular disease not measured in standard lipid panels.
            </CardDescription>
              <CardDetails>
              <CardDetailText>
                <strong>When to use:</strong> If you suspect elevated Lp(a) due to family history of premature 
                heart disease, persistent high LDL despite therapy, or previous cardiovascular events.
              </CardDetailText>
              
              <CardButtonContainer>
                <ActionButton 
                  type="primary"
                  size="large"
                  bgcolor="#000000"
                  hovercolor="#333333"
                  onClick={goToLPATest}
                >
                  Request LP(a) Test <RightOutlined />
                </ActionButton>
              </CardButtonContainer>
            </CardDetails>
          </StyledCard>
        </Col>
        
        <Col xs={24} md={8}>
          <StyledCard 
            headcolor="#1890FF" 
            bordercolor="#1890FF"
            title="Mobile Screening Intake"
          >
            <CardIcon color="#1890FF">
              <MedicineBoxFilled />
            </CardIcon>
            
            <CardDescription>
              Complete this form for BHAs mobile screening van in the Dallas–Fort Worth area.
              Covers a broader suite of health checks including blood pressure, glucose, and cholesterol.
            </CardDescription>
              <CardDetails>
              <CardDetailText>
                <strong>When to use:</strong> For on-site community screening events. This multi-vital form includes 
                demographics, medical history, and necessary consents for comprehensive screening.
              </CardDetailText>
              
              <CardButtonContainer>
                <ActionButton 
                  type="primary"
                  size="large"
                  bgcolor="#1890FF"
                  hovercolor="#40A9FF"
                  onClick={goToBusDataForm}
                >
                  Complete Screening Form <RightOutlined />
                </ActionButton>
              </CardButtonContainer>
            </CardDetails>
          </StyledCard>
        </Col>
        
        <Col xs={24} md={8}>
          <StyledCard 
            headcolor="#f0ad4e" 
            bordercolor="#f0ad4e"
            title="Volunteer Registration"
          >
            <CardIcon color="#f0ad4e">
              <TeamOutlined />
            </CardIcon>
            
            <CardDescription>
              Join our team of volunteers to help with BHA&apos;s screening events in Dallas and Houston.
              We welcome both medical and non-medical volunteers.
            </CardDescription>
              <CardDetails>
              <CardDetailText>
                <strong>When to use:</strong> If you&apos;re interested in volunteering at our upcoming community 
                health screening events. Medical professionals and general volunteers are both needed.
              </CardDetailText>
              
              <CardButtonContainer>
                <ActionButton 
                  type="primary"
                  size="large"
                  bgcolor="#f0ad4e"
                  hovercolor="#ec971f"
                  onClick={goToVolunteerForm}
                >
                  Volunteer Registration <RightOutlined />
                </ActionButton>
              </CardButtonContainer>
            </CardDetails>
          </StyledCard>
        </Col>
      </FormsRow>

      <Divider />
      
      {/* Upcoming Events Section */}
      <SectionTitle level={3}>Upcoming Screening Events</SectionTitle>
        {loading ? (
        <LoadingContainer>
          <Spin size="large" />
          <LoadingText>Loading upcoming events...</LoadingText>
        </LoadingContainer>
      ) : events.length > 0 ? (
        <EventsRow gutter={[24, 24]}>
          {events.map((event) => (
            <Col xs={24} sm={12} lg={8} key={event._id}>              <EventCard onClick={() => handleEventClick(event)}>
                <EventContainer>
                  <EventTitle level={4}>
                    {event.title}
                  </EventTitle>
                </EventContainer>
                  <EventMeta>
                  <MetaItem>
                    <CalendarOutlined />
                    <span>
                      {formatDate(event.startDate || event.date)}
                      {event.isMultiDay && event.bookingEndDate && (
                        <> – {formatDate(event.bookingEndDate)}</>
                      )}
                    </span>
                  </MetaItem>
                  <MetaItem>
                    <ClockCircleOutlined />
                    <span>{formatTime(event.startDate)} - {formatTime(event.endDate)}</span>
                  </MetaItem>
                  <MetaItem>
                    <EnvironmentOutlined />
                    <span>{event.location?.name || 'Location TBD'}</span>
                  </MetaItem>
                </EventMeta>
                  <EventDescription>
                  {event.description}
                </EventDescription>
              </EventCard>
            </Col>
          ))}
        </EventsRow>
      ) : (
        <EmptyEventsContainer>
          <EmptyEventsIcon>
            <CalendarOutlined />
          </EmptyEventsIcon>
          <EmptyEventsTitle level={4}>No Upcoming Events</EmptyEventsTitle>
          <EmptyEventsText>
            Check back soon for new screening events in your area.
          </EmptyEventsText>        </EmptyEventsContainer>
      )}
      
        <Divider style={{ margin: '48px 0 24px' }} />
      <FooterText>
        Questions? Contact us at <a href="mailto:info@blackheartassociation.org">info@blackheartassociation.org</a> or call 972-646-0031
      </FooterText>
      
      {/* Event Details Modal */}
      {selectedEvent && (
        <EventDetailsModal
          visible={eventModalVisible}
          event={selectedEvent}
          onClose={handleEventModalClose}
          onRegister={handleEventRegistration}
        />
      )}
    </HomeContainer>
  );
};

export default HomeScreen;